import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as TruckIcon,
  MyLocation as LocationIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { movilesAPI } from '../services/api';
import MapLocationPicker from '../components/MapLocationPicker';

export default function Moviles() {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMovil, setEditingMovil] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, movilId: null });
  const [mapPicker, setMapPicker] = useState({ open: false, type: null }); // 'origen' o 'destino'

  // Cargar móviles
  const { data: moviles, isLoading } = useQuery({
    queryKey: ['moviles'],
    queryFn: async () => {
      const response = await movilesAPI.listar();
      return response.data.data;
    },
  });

  // Mutación para crear móvil
  const crearMutation = useMutation({
    mutationFn: movilesAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries(['moviles']);
      handleCloseDialog();
      showSnackbar('Móvil creado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al crear móvil', 'error');
    },
  });

  // Mutación para actualizar móvil
  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }) => movilesAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['moviles']);
      handleCloseDialog();
      showSnackbar('Móvil actualizado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al actualizar móvil', 'error');
    },
  });

  // Mutación para eliminar móvil
  const eliminarMutation = useMutation({
    mutationFn: movilesAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries(['moviles']);
      setDeleteConfirm({ open: false, movilId: null });
      showSnackbar('Móvil eliminado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al eliminar móvil', 'error');
    },
  });

  function getEmptyForm() {
    return {
      IdMovil: '',
      Descripcion: '',
      Dominio: '',
      Capacidad: '',
      Domicilio_Origen: '',
      Lat_Origen: '',
      Lng_Origen: '',
      Domicilio_Destino: '',
      Lat_Destino: '',
      Lng_Destino: '',
      Color_Recorrido: '#751FE0',
      Activo: 1,
    };
  }

  const handleOpenDialog = (movil = null) => {
    if (movil) {
      setEditingMovil(movil);
      setFormData({
        IdMovil: movil.IdMovil,
        Descripcion: movil.Descripcion,
        Dominio: movil.Dominio,
        Capacidad: movil.Capacidad,
        Domicilio_Origen: movil.Domicilio_Origen,
        Lat_Origen: movil.Lat_Origen,
        Lng_Origen: movil.Lng_Origen,
        Domicilio_Destino: movil.Domicilio_Destino,
        Lat_Destino: movil.Lat_Destino,
        Lng_Destino: movil.Lng_Destino,
        Color_Recorrido: movil.Color_Recorrido,
        Activo: movil.Activo,
      });
    } else {
      setEditingMovil(null);
      setFormData(getEmptyForm());
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMovil(null);
    setFormData(getEmptyForm());
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.IdMovil) errors.IdMovil = 'ID Móvil es requerido';
    if (!formData.Descripcion) errors.Descripcion = 'Descripción es requerida';
    if (!formData.Capacidad || parseFloat(formData.Capacidad) <= 0) {
      errors.Capacidad = 'Capacidad debe ser mayor a 0';
    }
    if (!formData.Domicilio_Origen) errors.Domicilio_Origen = 'Domicilio de origen es requerido';
    if (!formData.Lat_Origen) errors.Lat_Origen = 'Latitud de origen es requerida';
    if (!formData.Lng_Origen) errors.Lng_Origen = 'Longitud de origen es requerida';
    if (!formData.Domicilio_Destino) errors.Domicilio_Destino = 'Domicilio de destino es requerido';
    if (!formData.Lat_Destino) errors.Lat_Destino = 'Latitud de destino es requerida';
    if (!formData.Lng_Destino) errors.Lng_Destino = 'Longitud de destino es requerida';
    if (!formData.Color_Recorrido || !/^#[0-9A-Fa-f]{6}$/.test(formData.Color_Recorrido)) {
      errors.Color_Recorrido = 'Color debe ser un código hexadecimal válido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      showSnackbar('Por favor complete todos los campos obligatorios', 'warning');
      return;
    }

    const data = {
      ...formData,
      Capacidad: parseFloat(formData.Capacidad),
      Lat_Origen: parseFloat(formData.Lat_Origen),
      Lng_Origen: parseFloat(formData.Lng_Origen),
      Lat_Destino: parseFloat(formData.Lat_Destino),
      Lng_Destino: parseFloat(formData.Lng_Destino),
    };

    if (editingMovil) {
      actualizarMutation.mutate({ id: editingMovil.Id, data });
    } else {
      crearMutation.mutate(data);
    }
  };

  const handleDeleteClick = async (movil) => {
    // Verificar si se puede eliminar
    try {
      const response = await movilesAPI.verificarEliminacion(movil.Id);
      if (response.data.data.canDelete) {
        setDeleteConfirm({ open: true, movilId: movil.Id });
      } else {
        showSnackbar(response.data.data.message, 'warning');
      }
    } catch (error) {
      showSnackbar('Error al verificar eliminación', 'error');
    }
  };

  const handleDeleteConfirm = () => {
    eliminarMutation.mutate(deleteConfirm.movilId);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleOpenMapPicker = (type) => {
    setMapPicker({ open: true, type });
  };

  const handleCloseMapPicker = () => {
    setMapPicker({ open: false, type: null });
  };

  const handleLocationSelected = (location) => {
    if (mapPicker.type === 'origen') {
      setFormData((prev) => ({
        ...prev,
        Lat_Origen: location.lat.toFixed(6),
        Lng_Origen: location.lng.toFixed(6),
        Domicilio_Origen: location.address,
      }));
      // Limpiar errores relacionados
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.Lat_Origen;
        delete newErrors.Lng_Origen;
        delete newErrors.Domicilio_Origen;
        return newErrors;
      });
    } else if (mapPicker.type === 'destino') {
      setFormData((prev) => ({
        ...prev,
        Lat_Destino: location.lat.toFixed(6),
        Lng_Destino: location.lng.toFixed(6),
        Domicilio_Destino: location.address,
      }));
      // Limpiar errores relacionados
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.Lat_Destino;
        delete newErrors.Lng_Destino;
        delete newErrors.Domicilio_Destino;
        return newErrors;
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TruckIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Móviles</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Nuevo Móvil
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Dominio</TableCell>
              <TableCell>Capacidad (m³)</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Color</TableCell>
              <TableCell align="center">Recolecciones</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : moviles && moviles.length > 0 ? (
              moviles.map((movil) => (
                <TableRow
                  key={movil.Id}
                  sx={{
                    backgroundColor: movil.Activo === 0 ? '#ffebee' : 'inherit',
                  }}
                >
                  <TableCell>{movil.IdMovil}</TableCell>
                  <TableCell>{movil.Descripcion}</TableCell>
                  <TableCell>{movil.Dominio}</TableCell>
                  <TableCell>{movil.Capacidad}</TableCell>
                  <TableCell>
                    <Chip
                      label={movil.Activo === 1 ? 'Activo' : 'Inactivo'}
                      color={movil.Activo === 1 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 40,
                        height: 20,
                        backgroundColor: movil.Color_Recorrido,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">{movil.Qty_items || 0}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(movil)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={movil.Qty_items > 0 ? 'No se puede eliminar (tiene recolecciones)' : 'Eliminar'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(movil)}
                          disabled={movil.Qty_items > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay móviles registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Formulario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMovil ? 'Editar Móvil' : 'Nuevo Móvil'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID Móvil *"
                value={formData.IdMovil}
                onChange={(e) => handleChange('IdMovil', e.target.value)}
                error={!!formErrors.IdMovil}
                helperText={formErrors.IdMovil}
                disabled={!!editingMovil}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dominio"
                value={formData.Dominio}
                onChange={(e) => handleChange('Dominio', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción *"
                value={formData.Descripcion}
                onChange={(e) => handleChange('Descripcion', e.target.value)}
                error={!!formErrors.Descripcion}
                helperText={formErrors.Descripcion}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacidad en m³ *"
                type="number"
                value={formData.Capacidad}
                onChange={(e) => handleChange('Capacidad', e.target.value)}
                error={!!formErrors.Capacidad}
                helperText={formErrors.Capacidad}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color de Recorrido *"
                type="color"
                value={formData.Color_Recorrido}
                onChange={(e) => handleChange('Color_Recorrido', e.target.value)}
                error={!!formErrors.Color_Recorrido}
                helperText={formErrors.Color_Recorrido}
              />
            </Grid>

            {/* Origen */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                Punto de Inicio del Recorrido
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domicilio de Origen *"
                value={formData.Domicilio_Origen}
                onChange={(e) => handleChange('Domicilio_Origen', e.target.value)}
                error={!!formErrors.Domicilio_Origen}
                helperText={formErrors.Domicilio_Origen}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitud Origen *"
                type="number"
                value={formData.Lat_Origen}
                onChange={(e) => handleChange('Lat_Origen', e.target.value)}
                error={!!formErrors.Lat_Origen}
                helperText={formErrors.Lat_Origen}
                inputProps={{ step: '0.000001' }}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Seleccionar ubicación en el mapa">
                      <IconButton size="small" onClick={() => handleOpenMapPicker('origen')} color="primary">
                        <LocationIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitud Origen *"
                type="number"
                value={formData.Lng_Origen}
                onChange={(e) => handleChange('Lng_Origen', e.target.value)}
                error={!!formErrors.Lng_Origen}
                helperText={formErrors.Lng_Origen}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>

            {/* Destino */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                Punto Final del Recorrido
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domicilio de Destino *"
                value={formData.Domicilio_Destino}
                onChange={(e) => handleChange('Domicilio_Destino', e.target.value)}
                error={!!formErrors.Domicilio_Destino}
                helperText={formErrors.Domicilio_Destino}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitud Destino *"
                type="number"
                value={formData.Lat_Destino}
                onChange={(e) => handleChange('Lat_Destino', e.target.value)}
                error={!!formErrors.Lat_Destino}
                helperText={formErrors.Lat_Destino}
                inputProps={{ step: '0.000001' }}
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Seleccionar ubicación en el mapa">
                      <IconButton size="small" onClick={() => handleOpenMapPicker('destino')} color="primary">
                        <LocationIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitud Destino *"
                type="number"
                value={formData.Lng_Destino}
                onChange={(e) => handleChange('Lng_Destino', e.target.value)}
                error={!!formErrors.Lng_Destino}
                helperText={formErrors.Lng_Destino}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.Activo === 1}
                    onChange={(e) => handleChange('Activo', e.target.checked ? 1 : 0)}
                  />
                }
                label="Móvil Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={crearMutation.isLoading || actualizarMutation.isLoading}
          >
            {crearMutation.isLoading || actualizarMutation.isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmación de Eliminación */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, movilId: null })}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Está seguro que desea eliminar este móvil?</Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, movilId: null })}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={eliminarMutation.isLoading}
          >
            {eliminarMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Map Location Picker */}
      <MapLocationPicker
        open={mapPicker.open}
        onClose={handleCloseMapPicker}
        onSelectLocation={handleLocationSelected}
        initialLat={mapPicker.type === 'origen' ? formData.Lat_Origen : formData.Lat_Destino}
        initialLng={mapPicker.type === 'origen' ? formData.Lng_Origen : formData.Lng_Destino}
        title={mapPicker.type === 'origen' ? 'Seleccionar Punto de Origen' : 'Seleccionar Punto de Destino'}
      />
    </Box>
  );
}
